import React, { useEffect, createElement } from 'react';
import { Box, Flex, Image, Text, Badge, IconButton } from '@chakra-ui/react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Home, Search, ShoppingCart, Package, User } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { Footer } from './Footer';
export const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    cart,
    adminSettings
  } = useAppContext();
  const cartItemsCount = cart.reduce((total, item) => total + item.quantity, 0);
  useEffect(() => {
    // Load AI Widget
    const script = document.createElement('script');
    script.id = 'omnidimension-web-widget';
    script.async = true;
    script.src = 'https://omnidim.io/web_widget.js?secret_key=73d4192fc04fcad4e4b7f5b4ae78e9ab';
    document.body.appendChild(script);
    return () => {
      const existingScript = document.getElementById('omnidimension-web-widget');
      if (existingScript) {
        document.body.removeChild(existingScript);
      }
    };
  }, []);
  const NavItem = ({
    to,
    icon,
    label,
    badge
  }: {
    to: string;
    icon: React.ReactNode;
    label: string;
    badge?: number;
  }) => {
    const isActive = location.pathname === to;
    return <Flex direction="column" align="center" justify="center" flex={1} py={2} cursor="pointer" color={isActive ? adminSettings.accentColor : 'gray.400'} onClick={() => navigate(to)} position="relative" transition="all 0.3s ease" _hover={{
      color: adminSettings.accentColor
    }} data-id="element-48">
        <Box transition="transform 0.2s" _hover={{
        transform: 'scale(1.1)'
      }} data-id="element-49">
          {icon}
        </Box>
        <Text fontSize="10px" mt={1} fontWeight={isActive ? '700' : '500'} letterSpacing="0.02em" data-id="element-50">
          {label}
        </Text>
        {badge !== undefined && badge > 0 && <Badge position="absolute" top={0} right={3} bg="red.500" color="white" borderRadius="full" fontSize="9px" minW="18px" h="18px" display="flex" alignItems="center" justifyContent="center" className="animate-pulse-badge" data-id="element-51">
            {badge}
          </Badge>}
      </Flex>;
  };
  if (adminSettings.maintenanceMode && location.pathname !== '/admin') {
    return <Flex minH="100vh" align="center" justify="center" bg="gray.50" direction="column" p={6} textAlign="center" data-id="element-52">
        <Image src="https://cdn.magicpatterns.com/uploads/cNqMS3US7kkJPPcN3Awd9h/1000066978.png" h="60px" mb={6} data-id="element-53" />
        <Text fontSize="2xl" fontWeight="800" color="gray.800" mb={2} data-id="element-54">
          We'll be back soon!
        </Text>
        <Text color="gray.500" data-id="element-55">
          Sky Brand is currently undergoing maintenance. Please check back
          later.
        </Text>
      </Flex>;
  }
  return <Box maxW="480px" mx="auto" bg="white" minH="100vh" position="relative" pb="72px" pt="56px" shadow="2xl" data-id="element-56">
      {/* Glassmorphism Header */}
      <Flex as="header" position="fixed" top={0} w="full" maxW="480px" h="56px" className="glass-effect" zIndex={20} align="center" px={4} justify="space-between" data-id="element-57">
        <Flex align="center" gap={2} cursor="pointer" onClick={() => navigate('/')} data-id="element-58">
          <Image src="https://cdn.magicpatterns.com/uploads/cNqMS3US7kkJPPcN3Awd9h/1000066978.png" alt="Sky Brand" h="36px" data-id="element-59" />
          <Text fontSize="lg" fontWeight="800" color={adminSettings.accentColor} letterSpacing="-0.02em" data-id="element-60">
            SKY BRAND
          </Text>
        </Flex>
        <Flex gap={1} data-id="element-61">
          <IconButton aria-label="Search" icon={<Search size={20} data-id="element-63" />} variant="ghost" color="gray.600" borderRadius="full" onClick={() => navigate('/products')} data-id="element-62" />
          <Box position="relative" data-id="element-64">
            <IconButton aria-label="Cart" icon={<ShoppingCart size={20} data-id="element-66" />} variant="ghost" color="gray.600" borderRadius="full" onClick={() => navigate('/cart')} data-id="element-65" />
            {cartItemsCount > 0 && <Badge position="absolute" top={0} right={0} bg="red.500" color="white" borderRadius="full" fontSize="9px" minW="16px" h="16px" display="flex" alignItems="center" justifyContent="center" data-id="element-67">
                {cartItemsCount}
              </Badge>}
          </Box>
        </Flex>
      </Flex>

      {/* Main Content */}
      <Box minH="calc(100vh - 128px)" className={adminSettings.animationsEnabled ? 'page-transition' : ''} data-id="element-68">
        <Outlet data-id="element-69" />

        {/* AI Widget Container */}
        <Box p={4} display="flex" justifyContent="center" data-id="element-70">
          <div id="omni-widget-component" style={{
          width: '100%',
          height: '500px',
          maxWidth: '400px'
        }} data-id="element-71"></div>
        </Box>

        <Footer data-id="element-72" />
      </Box>

      {/* Bottom Navigation */}
      <Flex as="nav" position="fixed" bottom={0} w="full" maxW="480px" h="72px" bg="white" borderTop="1px solid" borderColor="gray.100" zIndex={20} justify="space-around" align="center" shadow="0 -4px 20px rgba(0,0,0,0.06)" data-id="element-73">
        <NavItem to="/" icon={<Home size={22} data-id="element-75" />} label="Home" data-id="element-74" />
        <NavItem to="/products" icon={<Search size={22} data-id="element-77" />} label="Shop" data-id="element-76" />
        <NavItem to="/orders" icon={<Package size={22} data-id="element-79" />} label="Orders" data-id="element-78" />
        <NavItem to="/cart" icon={<ShoppingCart size={22} data-id="element-81" />} label="Cart" badge={cartItemsCount} data-id="element-80" />
        <NavItem to="/profile" icon={<User size={22} data-id="element-83" />} label="Profile" data-id="element-82" />
      </Flex>
    </Box>;
};